import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";

import { Icon } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";

import { getUrlForDownload } from "@/api/axios";
import { MemberDocument } from "@/api/types/Document";
import { ThemedButton } from "@/premium/themedComponents/ThemedButton";
import { ThemedH3 } from "@/premium/themedComponents/Headers";
import { Card } from "@/premium/themedComponents";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";

const ThemedIcon = styled(Icon)`
  path {
    fill: #61646c !important;
  }

  min-width: 2rem;
  min-height: 2rem;
  max-width: 2rem;
  max-height: 2rem;
`;

const ThemedP = styled.p`
  color: #313e48;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: left;
`;

const ThemedDocName = styled.p`
  font-family: ${(props) => props.theme.font};
  color: var(--neutral-dark-grey-dark-grey-500, #313e48);
  font-feature-settings: "clig" off, "liga" off;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
`;

const DocumentGrid = styled(Grid)`
  border-top: solid 1px lightgrey;
  padding-top: 20px;
  padding-bottom: 20px;
`;

interface DocumentRowProps {
  readonly memberDoc: MemberDocument;
  readonly openDocument: (doc: MemberDocument) => void;
  readonly downloadDocument: (doc: MemberDocument) => void;
}

const isIphoneUsingSafari = (() => {
  const isIphone = /iPhone/i.test(navigator.userAgent);
  const isSafari = !(/CriOS/i.test(navigator.userAgent))
    && !(/FxiOS/i.test(navigator.userAgent))
    && /Safari/i.test(navigator.userAgent);

  return isIphone && isSafari;
})();

interface DocumentListProps {
  readonly documents: MemberDocument[];
}

export const DocumentList: FC<DocumentListProps> = ({ documents }) => {
  const { t: translate } = useTranslation();

  const MobileDocumentRow: FC<DocumentRowProps> = ({ memberDoc, openDocument }) => {
    return (
      <DocumentGrid>
        <Stack spacing="none">
          <ThemedDocName>{memberDoc.name}</ThemedDocName>
          <ThemedP>{memberDoc.description}</ThemedP>
          <Stack.Spacer />
          <Shelf>
            <ThemedButton appearance="text" onClick={() => openDocument(memberDoc)}>
              <Shelf>
                <ThemedIcon name="link-out" size="regular" />
                {translate("PLAN.documentList.openInNewTap")}
              </Shelf>
            </ThemedButton>
            <Shelf.Spacer />
          </Shelf>
        </Stack>
      </DocumentGrid>
    );
  };

  const TabletDocumentRow: FC<DocumentRowProps> = ({ memberDoc, openDocument, downloadDocument }) => {
    return (
      <DocumentGrid>
        <Grid.Span columns={6}>
          <Shelf>
            <Icon name="document" />

            <Stack spacing="none">
              <ThemedDocName>{memberDoc.name}</ThemedDocName>
              <ThemedP>{memberDoc.description}</ThemedP>
            </Stack>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>

        <Grid.Span columns={6}>
          <Shelf>
            <Shelf.Spacer />
            <Stack>
              <ThemedButton appearance="text" onClick={() => openDocument(memberDoc)}>
                <Shelf>
                  <ThemedIcon name="link-out" size="regular" />
                  {translate("PLAN.documentList.openInNewTap")}
                </Shelf>
              </ThemedButton>

              <ThemedButton appearance="text" onClick={() => downloadDocument(memberDoc)}>
                <Shelf>
                  <ThemedIcon name="download" size="regular" />
                  {translate("PLAN.documentList.downloadPdf")}
                </Shelf>
              </ThemedButton>
            </Stack>
          </Shelf>
        </Grid.Span>
      </DocumentGrid>
    );
  };

  const DesktopDocumentRow: FC<DocumentRowProps> = ({ memberDoc, openDocument, downloadDocument }) => {
    return (
      <DocumentGrid>
        <Grid.Span columns={6}>
          <Shelf>
            <Icon name="document" />
            <Stack spacing="none">
              <ThemedDocName>{memberDoc.name}</ThemedDocName>
              <ThemedP>{memberDoc.description}</ThemedP>
            </Stack>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>

        <Grid.Span columns={6}>
          <Shelf>
            <Shelf.Spacer />
            <ThemedButton appearance="text" onClick={() => openDocument(memberDoc)}>
              <Shelf>
                <ThemedIcon name="link-out" size="regular" />
                {translate("PLAN.documentList.openInNewTap")}
              </Shelf>
            </ThemedButton>

            <ThemedButton appearance="text" onClick={() => downloadDocument(memberDoc)}>
              <Shelf>
                <ThemedIcon name="download" size="regular" />
                {translate("PLAN.documentList.downloadPdf")}
              </Shelf>
            </ThemedButton>
          </Shelf>
        </Grid.Span>
      </DocumentGrid>
    );
  };

  const { displayRules } = useResponsive(
    useMemo(() => {
      return {
        displayRules: {
          desktop: {
            documentRowComponent: DesktopDocumentRow,
          },
          tablet: {
            documentRowComponent: TabletDocumentRow,
          },
          mobile: {
            documentRowComponent: MobileDocumentRow,
          },
        },
      };
    }, []),
  );

  const openDoc = useCallback((url: string) => {
    if (isIphoneUsingSafari) {
      window.open(url, "_parent");
    } else {
      window.open(url, "_blank", "noreferrer");
    }
  }, []);

  const openDocument = useCallback((doc: MemberDocument) => {
    logEvent(ANALYTIC_EVENTS.PLAN_DOCUMENT.new_tab);
    getUrlForDownload(doc.id, "view").then((response) => {
      openDoc(response.url);
    });
  }, [openDoc]);

  const downloadDocument = useCallback((doc: MemberDocument) => {
    logEvent(ANALYTIC_EVENTS.PLAN_DOCUMENT.download);
    getUrlForDownload(doc.id, "download").then((response) => {
      openDoc(response.url);
    });
  }, [openDoc]);

  const hasDocs = documents.length > 0;

  return (
    <>
      <Card>
        <ThemedH3>{translate("PLAN.documentList.planDocuments")}</ThemedH3>
        {hasDocs && <ThemedP>{translate("PLAN.documentList.findInformationAboutYourPlan")}</ThemedP>}
        {!hasDocs && <ThemedP>{translate("PLAN.documentList.thereAreNoDocuments")}</ThemedP>}
        {hasDocs && (
          <Stack>
            {documents.map((memberDoc) => {
              return (
                <displayRules.documentRowComponent
                  key={memberDoc.id}
                  memberDoc={memberDoc}
                  openDocument={openDocument}
                  downloadDocument={downloadDocument}
                />
              );
            })}
          </Stack>
        )}
      </Card>
    </>
  );
};
